import { useEffect, useId, useMemo, useState } from 'react';
import { numberError } from '../config/ConfigKeyEditor';
import ConfigHistoryDialog from '../config/ConfigHistoryDialog';
import { sameValue, useConfigCard } from '../config/useConfigCard';
import { CardStateFrame, ConfigCardFrame, ConfigSwitch, ForbiddenNotice, money, NumberField } from './ConfigCardParts';
import ConfirmChangesDialog, { ChangeLine } from './ConfirmChangesDialog';
import { SERVICE_FEE_DEFAULTS, SERVICE_FEE_KEYS } from './financeConfigDefaults';
import InfoTip from './InfoTip';

export { SERVICE_FEE_KEYS } from './financeConfigDefaults';

export type FeeType = 'PERCENT' | 'FIXED';

const KEYS = Object.values(SERVICE_FEE_KEYS);
const LABELS: Record<string, string> = {
  [SERVICE_FEE_KEYS.enabled]: 'Service fee',
  [SERVICE_FEE_KEYS.percent]: 'Percent',
  [SERVICE_FEE_KEYS.fixedAed]: 'Fixed amount',
  [SERVICE_FEE_KEYS.capAed]: 'Cap',
};
const SAMPLES = [200, 500, 1500, 8000];

const num = (v: string | undefined) => {
  const n = Number((v ?? '').trim());
  return Number.isFinite(n) ? n : 0;
};

/** Fixed price iff a fixed amount is set and the percent is 0; anything else opens on Percentage. */
export function deriveFeeType(values: Record<string, string>): FeeType {
  return num(values[SERVICE_FEE_KEYS.fixedAed]) > 0 && num(values[SERVICE_FEE_KEYS.percent]) === 0 ? 'FIXED' : 'PERCENT';
}

/** Round half up to 2 decimals, as orders does (Spec 068 §6). */
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * The values a save writes for a fee type (decision 5, BR-10): Percentage zeroes the fixed amount;
 * Fixed price zeroes the percent and the cap (A4 — orders applies min(fee, cap) to a fixed fee too).
 */
export function effectiveFeeValues(draft: Record<string, string>, type: FeeType): Record<string, string> {
  const out = { ...draft };
  if (type === 'PERCENT') out[SERVICE_FEE_KEYS.fixedAed] = '0';
  else {
    out[SERVICE_FEE_KEYS.percent] = '0';
    out[SERVICE_FEE_KEYS.capAed] = '0';
  }
  return out;
}

const describeType = (type: FeeType, v: Record<string, string>) => (type === 'FIXED'
  ? `New bookings pay a fixed AED ${money(num(v[SERVICE_FEE_KEYS.fixedAed]))} each, with no cap.`
  : `New bookings pay ${num(v[SERVICE_FEE_KEYS.percent])}% of the service price${num(v[SERVICE_FEE_KEYS.capAed]) > 0
    ? `, capped at AED ${money(num(v[SERVICE_FEE_KEYS.capAed]))}` : ''}.`);

/**
 * #1113 — Finance → Configuration: the platform service fee, edited in place (was read-only,
 * Spec 068 AC-25). One fee type at a time: Percentage (with an optional cap) or Fixed price. The four
 * `garagat.service-fee.*` keys are saved in one atomic request, with the same audit as Runtime Config.
 * Turning the fee on or changing its type asks for confirmation first. The server stays the source of
 * truth for the fee: the example line here is only an illustration.
 */
export default function ServiceFeeCard({ token }: { token: string }) {
  const card = useConfigCard({ keys: KEYS, screen: 'finance-config', defaults: SERVICE_FEE_DEFAULTS, token });
  const { draft, server, entries } = card;
  const serverType = useMemo(() => deriveFeeType(server), [server]);
  const [type, setType] = useState<FeeType>(serverType);
  const [sample, setSample] = useState(500);
  const [confirming, setConfirming] = useState(false);
  const [history, setHistory] = useState(false);
  const radioName = useId();

  // A fresh server copy (load, save) re-derives the type.
  useEffect(() => { setType(serverType); }, [serverType]);

  if (card.load.kind === 'loading') {
    return (
      <CardStateFrame testId="service-fee-card" title="Service fee">
        <div className="admin-loading" aria-live="polite"><div className="admin-loading-spinner" /></div>
      </CardStateFrame>
    );
  }
  if (card.load.kind === 'error') {
    return (
      <CardStateFrame testId="service-fee-card" title="Service fee">
        <div className="admin-info-banner danger" role="alert" data-testid="service-fee-load-error">{card.load.message}</div>
        <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" onClick={() => card.reload()}>Try again</button>
      </CardStateFrame>
    );
  }
  if (card.load.kind === 'missing') {
    return (
      <CardStateFrame testId="service-fee-card" title="Service fee">
        <div className="admin-info-banner warning" role="status" data-testid="service-fee-unavailable">
          The service fee settings are not available to your account on this environment. Reload the page, or ask a super admin.
        </div>
      </CardStateFrame>
    );
  }

  const enabled = draft[SERVICE_FEE_KEYS.enabled] === 'true';
  const serverEnabled = server[SERVICE_FEE_KEYS.enabled] === 'true';
  const fixedMode = type === 'FIXED';
  const visibleKeys: string[] = fixedMode ? [SERVICE_FEE_KEYS.fixedAed] : [SERVICE_FEE_KEYS.percent, SERVICE_FEE_KEYS.capAed];
  const errorOf = (key: string) => card.fieldErrors[key]
    ?? (visibleKeys.includes(key) && entries.get(key) ? numberError(entries.get(key)!, draft[key] ?? '') : null);
  const invalid = visibleKeys.filter((k) => errorOf(k));
  const effective = effectiveFeeValues(draft, type);
  const changes = card.changesFrom(effective);
  const dirty = changes.length > 0;
  const edited = dirty || type !== serverType || KEYS.some((k) => draft[k] !== server[k]);
  const bothSet = num(server[SERVICE_FEE_KEYS.percent]) > 0 && num(server[SERVICE_FEE_KEYS.fixedAed]) > 0;

  const hintRange = (key: string) => {
    const e = entries.get(key);
    return e && e.min !== null && e.max !== null ? `${e.min}–${e.max}.` : '';
  };
  const deployed = (key: string) => !entries.get(key)?.overridden;

  const fmt = (key: string, v: string) => {
    if (key === SERVICE_FEE_KEYS.enabled) return v === 'true' ? 'On' : 'Off';
    if (key === SERVICE_FEE_KEYS.percent) return `${num(v)}%`;
    return `AED ${money(num(v))}`;
  };
  const changeLines = (): ChangeLine[] => {
    const lines: ChangeLine[] = [];
    if (type !== serverType) {
      lines.push({ label: 'Fee type', from: serverType === 'FIXED' ? 'Fixed price' : 'Percentage', to: fixedMode ? 'Fixed price' : 'Percentage' });
    }
    for (const c of changes) {
      const notEdited = forced.some((f) => f.key === c.key);
      lines.push({ label: `${LABELS[c.key]}${notEdited ? ' (not edited, set by the fee type)' : ''}`, from: fmt(c.key, server[c.key]), to: fmt(c.key, c.value) });
    }
    return lines;
  };

  const turningOn = enabled && !serverEnabled;
  const typeChanged = type !== serverType;
  // Keys the save writes that the admin never edited: the other amount (and the cap) zeroed by the fee
  // type, e.g. a legacy "both set" fee or a legacy cap in Fixed mode. A live fee never changes silently.
  const forced = changes.filter((c) => sameValue(entries.get(c.key), draft[c.key] ?? '', server[c.key] ?? ''));
  const forcedNames = forced.map((c) => LABELS[c.key].toLowerCase());
  const forcedNote = forced.length
    ? ` Also sets the ${joinNames(forcedNames)} to 0, which you did not edit: ${fixedMode
      ? 'a fixed price has no percent and no cap' : 'a percentage fee has no fixed amount'}.`
    : '';
  const confirmTitle = turningOn
    ? 'Start charging the service fee?'
    : typeChanged
      ? `Switch the fee to ${fixedMode ? 'a fixed price' : 'a percentage'}?`
      : `Also set the ${joinNames(forcedNames)} to 0?`;
  const confirmBody = turningOn
    ? `Customers on app versions that show the fee pay it on every new booking, starting about a minute after you save. ${describeType(type, effective)}${forcedNote}`
    : typeChanged
      ? `${describeType(type, effective)} Starts about a minute after you save; bookings already made keep their fee.${forcedNote}`
      : `${describeType(type, effective)}${forcedNote} Starts about a minute after you save.`;
  const confirmLabel = turningOn ? 'Save and turn on' : typeChanged ? 'Save fee type' : 'Save changes';

  const submit = () => {
    if (invalid.length) {
      document.getElementById(fieldId(invalid[0]))?.focus();
      return;
    }
    if (!dirty) return;
    if (turningOn || typeChanged || forced.length) setConfirming(true);
    else void card.save(changes);
  };

  const exampleLine = () => {
    if (invalid.length) return <span className="config-dim">Fix the fields above to see the fee.</span>;
    let line: string;
    let capNote = '';
    if (fixedMode) {
      const fee = num(draft[SERVICE_FEE_KEYS.fixedAed]);
      line = `Every booking: AED ${money(fee)} (incl. VAT ${money(round2((fee * 5) / 105))})`;
    } else {
      const p = num(draft[SERVICE_FEE_KEYS.percent]);
      const cap = num(draft[SERVICE_FEE_KEYS.capAed]);
      const raw = round2((sample * p) / 100);
      const fee = cap > 0 ? Math.min(raw, cap) : raw;
      if (cap > 0) capNote = raw > cap ? ` · capped at AED ${money(cap)}` : ` · under the AED ${money(cap)} cap`;
      line = `${p}% → AED ${money(fee)} (incl. VAT ${money(round2((fee * 5) / 105))})`;
    }
    return (
      <>
        {!enabled && <span className="config-dim">No fee while off. When on: </span>}
        <b>{line}</b>
        {capNote && <span className="config-dim">{capNote}</span>}
      </>
    );
  };

  return (
    <>
      <ConfigCardFrame
        testId="service-fee-card"
        title="Service fee"
        status={(
          <>
            <InfoTip glossaryKey="serviceFee" />
            <span className={`admin-badge ${serverEnabled ? 'approved' : 'neutral'}`} data-testid="service-fee-status">
              {serverEnabled ? 'On' : 'Off'}
            </span>
          </>
        )}
        dirty={dirty}
        sub="What Garagat charges the customer per booking, on top of the service price. VAT-inclusive."
        lastChanged={card.lastChanged}
        onHistory={() => setHistory(true)}
        footNote="Applies to new bookings only. Live within a minute. Same audit as Runtime Config."
        footError={card.formError}
        invalidCount={invalid.length}
        saving={card.saving}
        canSave={dirty && invalid.length === 0 && !card.forbidden}
        canDiscard={edited}
        onDiscard={() => { card.discard(); setType(serverType); }}
        onSubmit={submit}
        toast={card.toast}
      >
        {card.forbidden && <ForbiddenNotice onReload={() => card.reload()} />}
        {bothSet && (
          <div className="admin-info-banner warning config-banner" role="status" data-testid="service-fee-both-set">
            <span>
              <b>Both a percent and a fixed amount are set</b> — pick one fee type and save. Saving Percentage sets the
              fixed amount to 0.
            </span>
          </div>
        )}

        <ConfigSwitch
          id="service-fee-enabled"
          checked={enabled}
          onChange={(on) => card.setField(SERVICE_FEE_KEYS.enabled, on ? 'true' : 'false')}
          label="Charge the service fee on new bookings"
          description="Off charges no fee on new bookings. Bookings already made keep the fee they were shown. Only app versions that can show the fee are charged it."
        />

        <fieldset className="config-fee-type">
          <legend>Fee type</legend>
          <div className="config-seg-radio">
            <input
              type="radio" id="service-fee-type-percent" name={radioName} value="PERCENT"
              checked={!fixedMode} onChange={() => setType('PERCENT')}
            />
            <label htmlFor="service-fee-type-percent">Percentage</label>
            <input
              type="radio" id="service-fee-type-fixed" name={radioName} value="FIXED"
              checked={fixedMode} onChange={() => setType('FIXED')}
            />
            <label htmlFor="service-fee-type-fixed">Fixed price</label>
          </div>
          <div className="config-hint">Pick one. A booking pays either a percent of its service price or one fixed amount, never both.</div>
        </fieldset>

        <div className="config-grid" style={{ marginTop: '1rem', maxWidth: 560 }}>
          {!fixedMode && (
            <NumberField
              id={fieldId(SERVICE_FEE_KEYS.percent)}
              label="Percent of service price"
              unit="%"
              value={draft[SERVICE_FEE_KEYS.percent] ?? ''}
              onChange={(v) => card.setField(SERVICE_FEE_KEYS.percent, v)}
              hint={`${hintRange(SERVICE_FEE_KEYS.percent)} 5 means 5% of the price the customer sees.`}
              error={errorOf(SERVICE_FEE_KEYS.percent)}
              deployed={deployed(SERVICE_FEE_KEYS.percent)}
            />
          )}
          {fixedMode && (
            <NumberField
              id={fieldId(SERVICE_FEE_KEYS.fixedAed)}
              label="Fixed amount"
              unit="AED"
              unitBefore
              value={draft[SERVICE_FEE_KEYS.fixedAed] ?? ''}
              onChange={(v) => card.setField(SERVICE_FEE_KEYS.fixedAed, v)}
              hint={`${hintRange(SERVICE_FEE_KEYS.fixedAed)} The same amount on every booking, incl. VAT.`}
              error={errorOf(SERVICE_FEE_KEYS.fixedAed)}
              deployed={deployed(SERVICE_FEE_KEYS.fixedAed)}
            />
          )}
          {!fixedMode && (
            <NumberField
              id={fieldId(SERVICE_FEE_KEYS.capAed)}
              label="Cap per booking"
              unit="AED"
              unitBefore
              value={draft[SERVICE_FEE_KEYS.capAed] ?? ''}
              onChange={(v) => card.setField(SERVICE_FEE_KEYS.capAed, v)}
              hint={`${hintRange(SERVICE_FEE_KEYS.capAed)} 0 = no cap.`}
              error={errorOf(SERVICE_FEE_KEYS.capAed)}
              deployed={deployed(SERVICE_FEE_KEYS.capAed)}
            />
          )}
        </div>
        {fixedMode && (
          <div className="config-hint" style={{ marginTop: '0.5rem' }} data-testid="service-fee-no-cap">
            No cap in Fixed price mode. Saving sets the cap to 0.
          </div>
        )}

        <div className="config-example" aria-live="polite" data-testid="service-fee-example">
          {!fixedMode && (
            <>
              <span>On a</span>
              <label htmlFor="service-fee-sample" className="config-sr-only">Example service price</label>
              <select id="service-fee-sample" value={sample} onChange={(e) => setSample(Number(e.target.value))}>
                {SAMPLES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <span>AED service:</span>
            </>
          )}
          <span>{exampleLine()}</span>
          <span className="config-dim">(example)</span>
        </div>
      </ConfigCardFrame>

      {confirming && (
        <ConfirmChangesDialog
          title={confirmTitle}
          body={confirmBody}
          confirmLabel={confirmLabel}
          changes={changeLines()}
          onCancel={() => setConfirming(false)}
          onConfirm={() => { setConfirming(false); void card.save(changes); }}
        />
      )}
      {history && (
        <ConfigHistoryDialog
          title="Service fee history"
          keys={KEYS}
          labels={LABELS}
          sensitiveKeys={KEYS.filter((k) => entries.get(k)?.sensitive)}
          maskSensitive
          token={token}
          onClose={() => setHistory(false)}
        />
      )}
    </>
  );
}

function fieldId(key: string): string {
  return `service-fee-${key.split('.').pop()}`;
}

/** "fixed amount", "percent and cap". */
function joinNames(names: string[]): string {
  return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
